// @flow
import orderBy from 'lodash/orderBy'
import split from 'lodash/split'
import values from 'lodash/values'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Grid from 'ui/atoms/Grid'
import Heading from 'ui/atoms/Heading'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import {
  ADVANCED_QUESTIONS_FOR_BUILDER,
  QUESTION_FOR_FORM_BUILDER,
} from 'utils/constants'
import { SELECTION_TYPE } from 'utils/FormBuilder/ConstantsFormBuilder'
import { ZentakeContext } from 'utils/ZentakeContext'

import QuestionTypeItem from './QuestionTypeItem'

const questionTypeList = orderBy(
  values(QUESTION_FOR_FORM_BUILDER),
  'formBuilderIndex'
)

const advancedQuestionTypeList = orderBy(
  values(ADVANCED_QUESTIONS_FOR_BUILDER),
  'formBuilderIndex'
)

function QuestionTypesList(): React.Node {
  const { teamDetails } = React.useContext(ZentakeContext)
  const {
    selectedItem,
    setSelectedItem,
    getSection,
    getPage,
    addRow,
    addRepeatingSectionAfter,
    addQuestion,
    addMultipleQuestions,
    getRow,
    shouldHideCreditCardQuestion,
    addChildPageWithQuestion,
  } = React.useContext(FormBuilderContext)

  const getSectionAndRowId = React.useCallback(() => {
    if (selectedItem.type === SELECTION_TYPE.QUESTION_ADD) {
      const rowId = split(selectedItem.id, '_')[0]
      const row = getRow(rowId)
      return [getSection(row.section), rowId]
    }

    if (selectedItem.type === SELECTION_TYPE.ROW_ADD) {
      return [getSection(selectedItem.id), null]
    }

    return [null, null]
  }, [getRow, getSection, selectedItem.id, selectedItem.type])

  const [section] = getSectionAndRowId()
  const page = section ? getPage(section.page) : null

  const isRepeatingSection = page?.repeat_section

  const createChildPageAndAddQuestion = React.useCallback(
    (idForBuilder, questionTypeId) => {
      const questionType =
        QUESTION_FOR_FORM_BUILDER[idForBuilder] ||
        ADVANCED_QUESTIONS_FOR_BUILDER[idForBuilder]

      let calculationValueType = undefined
      if (idForBuilder === 'calculation') calculationValueType = 'number'

      const newQuestion = addChildPageWithQuestion(
        selectedItem.parentPageId,
        {
          question_type: questionType.overrideId || questionTypeId,
          question_subtype: questionType.subtypes[0].id,
        },
        {
          text: '',
          id_for_builder: idForBuilder,
          order: 0,
          display_columns: idForBuilder === 'full_name' ? 12 : 6,
          calculation_value_type: calculationValueType,
        }
      )

      if (!newQuestion) return

      return setSelectedItem({
        id: newQuestion.id,
        type: SELECTION_TYPE.QUESTION,
      })
    },
    [selectedItem.parentPageId, addChildPageWithQuestion, setSelectedItem]
  )

  const addAddressQuestions = React.useCallback(() => {
    // TODO: move this to a more appropriate location
    const [section, rowId] = getSectionAndRowId()

    if (!section) return

    const states = [
      'AL',
      'AK',
      'AZ',
      'AR',
      'CA',
      'CZ',
      'CO',
      'CT',
      'DE',
      'DC',
      'FL',
      'GA',
      'GU',
      'HI',
      'ID',
      'IL',
      'IN',
      'IA',
      'KS',
      'KY',
      'LA',
      'ME',
      'MD',
      'MA',
      'MI',
      'MN',
      'MS',
      'MO',
      'MT',
      'NE',
      'NV',
      'NH',
      'NJ',
      'NM',
      'NY',
      'NC',
      'ND',
      'OH',
      'OK',
      'OR',
      'PA',
      'PR',
      'RI',
      'SC',
      'SD',
      'TN',
      'TX',
      'UT',
      'VT',
      'VI',
      'VA',
      'WA',
      'WV',
      'WI',
      'WY',
    ]

    const questions = [
      {
        question_type: {
          question_type: 'text',
          question_subtype: 'single_line',
        },
        text: 'Address',
        id_for_builder: 'text',
        display_columns: 5,
      },
      {
        question_type: {
          question_type: 'text',
          question_subtype: 'single_line',
        },
        text: 'City',
        id_for_builder: 'text',
        display_columns: 3,
      },
      {
        question_type: {
          question_type: 'radio',
          question_subtype: 'dropdown',
        },
        text: 'State',
        id_for_builder: '"dropdown_for_builder"',
        display_columns: 2,
        question_choices: states,
      },
      {
        question_type: {
          question_type: 'text',
          question_subtype: 'single_line',
        },
        text: 'Zipcode',
        id_for_builder: 'text',
        display_columns: 2,
      },
    ]
    addMultipleQuestions(section.id, rowId, questions, true)
    setSelectedItem({})
  }, [getSectionAndRowId, addMultipleQuestions, setSelectedItem])

  const onSelectQuestionType = React.useCallback(
    (idForBuilder, questionTypeId) => {
      if (selectedItem.parentPageId) {
        return createChildPageAndAddQuestion(idForBuilder, questionTypeId)
      }

      const questionType =
        QUESTION_FOR_FORM_BUILDER[idForBuilder] ||
        ADVANCED_QUESTIONS_FOR_BUILDER[idForBuilder]

      let calculationValueType = undefined

      if (idForBuilder === 'calculation') calculationValueType = 'number'

      if (selectedItem.type === SELECTION_TYPE.ROW_ADD) {
        const section = getSection(selectedItem.id)

        const newQuestion = addRow(
          section.page,
          selectedItem.id,
          {
            question_type: questionType.overrideId || questionTypeId,
            question_subtype: questionType.subtypes[0].id,
          },
          {
            text: '',
            id_for_builder: idForBuilder,
            order: 0,
            display_columns: idForBuilder === 'full_name' ? 12 : 6,
            calculation_value_type: calculationValueType,
          }
        )

        if (!newQuestion) return

        return setSelectedItem({
          id: newQuestion.id,
          type: SELECTION_TYPE.QUESTION,
        })
      }

      if (selectedItem.type === SELECTION_TYPE.QUESTION_ADD) {
        const [rowId, display_columns] = split(selectedItem.id, '_')

        const newQuestion = addQuestion(rowId, {
          display_columns: idForBuilder === 'full_name' ? 12 : display_columns,
          question_type: {
            question_type: questionType.overrideId || questionTypeId,
            question_subtype: questionType.subtypes[0].id,
          },
          id_for_builder: idForBuilder,
          text: '',
          calculation_value_type: calculationValueType,
        })

        if (!newQuestion) return

        return setSelectedItem({
          id: newQuestion.id,
          type: SELECTION_TYPE.QUESTION,
        })
      }
    },
    [
      selectedItem,
      addRow,
      getSection,
      addQuestion,
      setSelectedItem,
      createChildPageAndAddQuestion,
    ]
  )

  const renderQuestionType = React.useCallback(
    (questionType) => {
      if (shouldHideCreditCardQuestion(questionType.id)) return null
      const isDisabled =
        isRepeatingSection &&
        [
          'summary',
          'calculation',
          'signature',
          'file_upload_for_builder',
          'table',
          'matrix',
        ].includes(questionType.idForBuilder)

      return (
        <QuestionTypeItem
          disabled={isDisabled}
          id={questionType.id}
          idForBuilder={questionType.idForBuilder}
          key={questionType.idForBuilder}
          name={questionType.name}
          onClick={() => {
            if (questionType.name === 'Address') {
              return addAddressQuestions()
            }
            onSelectQuestionType(questionType.idForBuilder, questionType.id)
          }}
        />
      )
    },
    [
      addAddressQuestions,
      onSelectQuestionType,
      shouldHideCreditCardQuestion,
      isRepeatingSection,
    ]
  )

  return (
    <Space size="large">
      <Heading>Add Field</Heading>
      <div>
        <Text $light $small>
          Add Field
        </Text>
        <Grid cols={2} gap="small">
          {questionTypeList.map(renderQuestionType)}
        </Grid>
      </div>
      <div>
        <Text $light $small>
          Advanced Fields
        </Text>
        <Grid cols={2} gap="small">
          {advancedQuestionTypeList
            .filter((questionType) => {
              return questionType.id !== 'summary' || teamDetails?.intake_ai
            })
            .map(renderQuestionType)}
          {teamDetails.repeat_section ? (
            <QuestionTypeItem
              disabled={isRepeatingSection}
              id="repeating-section"
              idForBuilder="repeating-section"
              key="repeating-section"
              name="Repeating Section"
              onClick={() => addRepeatingSectionAfter(page.id)}
            />
          ) : null}
        </Grid>
      </div>
    </Space>
  )
}

export default QuestionTypesList
